// @ts-nocheck
/**
 * AppShell Component
 *
 * Main application layout wrapper for authenticated routes with complete navigation system.
 * Migrated from components/AppShell.tsx to use proper componentization and React Router v6.
 *
 * Features:
 * - Vertical icon sidebar (desktop only) with 10 primary tabs
 * - Top navigation bar with breadcrumbs and utility icons
 * - Campaign selector toolbar with sync button
 * - Mobile sidebar with hamburger menu
 * - Bottom status bar
 * - Toast notifications (react-hot-toast)
 * - Startup loading overlay
 * - Settings and campaign settings modals
 * - Auth state management and redirects
 * - Cache sync (SSE connection)
 * - Route calculation toast notifications
 * - Force desktop view toggle (developer mode)
 * - Web vitals tracking
 *
 * Architecture:
 * - Uses React Router v6 <Outlet /> for nested routes
 * - Proper separation of concerns with extracted components
 * - Mobile-first responsive design
 * - Accessibility: WCAG 2.1 compliant with ARIA labels
 */

import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { Layout, Database, FileCode, Activity, Map, Folder, TrendingUp, Settings, HelpCircle, LogOut, Monitor, LayoutDashboard, BookOpen, Calendar, FileText, Home, MessageCircle } from 'lucide-react';
import { useTypedNavigation } from '@shared/hooks/routing/useNavigation';
import { useBackForwardHandler } from '@shared/hooks/routing/useBackForwardHandler';
import { useModalRoute } from '@shared/hooks/routing/useModalRoute';
import { initWebVitals } from '@shared/utils/webVitals';
import { APP_TABS } from '@app/constants';
import { AppTab } from '@shared/types/ui-components';
import { ROUTES, TAB_TO_ROUTE, ROUTE_TO_TAB } from '@app/routes';
import { Button, SettingsModal, MobileSidebar } from '../ui';
import { SyncButton, StartupLoadingOverlay, CopyDevLogButton } from '@shared/components/cache';
import { useCacheSync, useIsMobile, useTokenBudgetMonitor } from '@shared/hooks';
import { CampaignSettingsModal } from '../modals/CampaignSettingsModal';
import { useCampaignStore } from '@features/campaigns/store/campaignStore';
import { useContactStore } from '@features/contacts/store/contactStore';
import { useOrganizationStore } from '@features/organizations/store/organizationStore';
import { useRouteStore } from '@features/routes/store/routeStore';
import { useRouteCalculationToast } from '@features/routes/hooks';
import { useEnrichmentProgress } from '@shared/hooks/useEnrichmentProgress';
import { FloatingProgressTracker } from '@shared/components/progress';
import { useAuthStore } from '@shared/store/authStore';
import { DeveloperSettings } from '@shared/utils/developerSettings';
import { AppLogo } from '@shared/components/branding/AppLogo';
import { useUIStore } from '@shared/store/uiStore';

// Extracted layout components
import { VerticalSidebar } from './VerticalSidebar';
import { TopBar } from './TopBar';
import { PhaseSelector } from './PhaseSelector';
import { RightSidebar } from './RightSidebar';
import { MobileBottomToolbar } from './MobileBottomToolbar';

// User Stickies (appears on all /app pages)
import { UserStickies } from '@shared/components/UserStickies';
import { useUserStickiesStore } from '@shared/store/useUserStickiesStore';

// Tab Stickies (per-tab sticky notes with PostgreSQL persistence)
import { TabStickies } from '@shared/components/TabStickies';

// Pocket Sticky Notes - Goals & Focus of the Week (global, persists across all pages)
import { PocketStickyNotes } from '@/features/dashboard';

// Minimized URL Panels (appears on all /app pages via Portal)
import { MinimizedUrlPanelsDock } from './MinimizedUrlPanelsDock';
import { GlobalLinkPreviewPanel } from './GlobalLinkPreviewPanel';

// App Panel (open any page as a side panel from AppLauncher)
import { AppPanel } from './AppPanel';

// App Panel Taskbar (Windows-style taskbar for minimized AppPanels)
import { AppPanelTaskbar, APP_PANEL_TASKBAR_HEIGHT } from './AppPanelTaskbar';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';

// Floating Timer (global, persists across all /app pages)
import { FloatingTimer } from '@/components/FloatingTimer/FloatingTimer';

// UserIcon component (must be defined before TabIcons)
const UserIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Tab Icons (14px size for vertical sidebar)
const TabIcons: Record<AppTab, React.ReactNode> = {
  'Home': <Home size={14} />,
  'Dashboard': <LayoutDashboard size={14} />,
  'AI Assistant': <MessageCircle size={14} />,
  'Campaign Management': <Folder size={14} />,
  'Organization Management': <Layout size={14} />,
  'Address Management': <Database size={14} />,
  'Route Planning': <TrendingUp size={14} />,
  'Review Routes': <Map size={14} />,
  'Contact Management': <UserIcon size={14} />,
  'Label Generator': <FileCode size={14} />,
  'Execution Tracking': <Activity size={14} />,
  'Handwrytten Export': <FileCode size={14} />,
  'Event Planning Grid': <Layout size={14} />,
  'Timelines': <Calendar size={14} />,
  'Notes': <BookOpen size={14} />,
  'Reports': <FileCode size={14} />,
  'Report Generator': <FileText size={14} />,
};

export const AppShell: React.FC = () => {
  const { navigateTo } = useTypedNavigation();
  const location = useLocation();

  // Handle browser back/forward button navigation
  useBackForwardHandler();

  // ===== ALL HOOKS MUST BE DECLARED BEFORE ANY CONDITIONAL RETURNS =====

  // Auth Store
  const { logout: authLogout, isAuthenticated } = useAuthStore();

  // Campaign Store
  const { campaigns, selectedCampaignId, setSelectedCampaign, fetchCampaigns } = useCampaignStore();

  // Contact Store - for expense allocation tables
  const { fetchContacts } = useContactStore();

  // Organization Store - for expense allocation tables
  const { fetchOrganizations } = useOrganizationStore();

  // Route Store - for fetching active route on app load
  const { fetchActiveRoute, fetchRouteHistory, loadRoute } = useRouteStore();

  // Auth check state - prevents redirect loops on render flickering
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);

  // Theme State (using uiStore for persistence and cloud sync)
  const { themeClass: currentTheme, setThemeClass: setCurrentTheme, focusMode } = useUIStore();
  const isMobile = useIsMobile();

  // Modal routing hook for settings (replaces isSettingsOpen state)
  const { open: openSettings } = useModalRoute('settings');

  // Toolbar Menus
  const [isCampaignMenuOpen, setIsCampaignMenuOpen] = useState(false);
  const [isCampaignSettingsOpen, setIsCampaignSettingsOpen] = useState(false);

  // Mobile Navigation
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Force Desktop View State
  const [forceDesktopView, setForceDesktopView] = useState(false);

  // Floating Timer visibility (persists in localStorage)
  const [isTimerVisible, setIsTimerVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('floating-timer-visible');
      return stored !== 'false'; // Default to visible
    } catch {
      return true;
    }
  });

  // Cache sync hook (SSE connection for real-time updates)
  useCacheSync();

  // Route calculation toast hook (persistent progress notification across tabs)
  useRouteCalculationToast();

  // Enrichment progress hook (floating tracker for HMC event enrichment)
  useEnrichmentProgress();

  // Token budget monitoring hook (polls every 5 minutes, shows warning at 80%)
  useTokenBudgetMonitor();

  // Dock display mode - when 'collapsed' or 'hidden', also hide floating timer and restore button
  const dockDisplayMode = useUserStickiesStore((s) => s.dockDisplayMode);
  const isDockHidden = dockDisplayMode === 'hidden' || dockDisplayMode === 'collapsed';

  // Taskbar visibility - floating elements need to account for taskbar height
  const isTaskbarVisible = useAppPanelStore((s) => s.isTaskbarVisible);
  const isAssistantRoute = location.pathname.startsWith('/app/assistant');

  // Initialize Web Vitals tracking on mount
  useEffect(() => {
    initWebVitals();
  }, []);

  // Global keyboard shortcuts (S = Search, A = App Launcher)
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input, textarea, or contenteditable
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]');

      if (isTyping) return;

      // Ignore if any modifier keys are pressed
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      switch (event.key.toLowerCase()) {
        case 's':
          event.preventDefault();
          window.dispatchEvent(new CustomEvent('focus-search-bar'));
          break;
        case 'a':
          event.preventDefault();
          window.dispatchEvent(new CustomEvent('open-app-launcher'));
          break;
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Listen for auth:session-expired events from apiClient (prevents hard page refresh loops)
  useEffect(() => {
    const handleAuthExpired = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[AppShell] Auth session expired, forcing logout', customEvent.detail);

      // CRITICAL: Clear auth state BEFORE navigating to prevent redirect loops
      // The apiClient already cleared localStorage, but also reset Zustand state
      try {
        await useAuthStore.getState().logout();
      } catch (error) {
        console.error('[AppShell] Logout failed during session expiry handling:', error);
        // Fallback: force clear localStorage if logout fails
        localStorage.removeItem('dms-auth-storage');
      }

      // Now navigate to login (with auth state cleared)
      navigateTo(ROUTES.LOGIN, undefined, undefined, { replace: true });
    };

    window.addEventListener('auth:session-expired', handleAuthExpired);
    return () => window.removeEventListener('auth:session-expired', handleAuthExpired);
  }, [navigateTo]);

  // Listen for open-mobile-sidebar events from page headers
  useEffect(() => {
    const handleOpenSidebar = () => {
      setIsMobileSidebarOpen(true);
    };

    window.addEventListener('open-mobile-sidebar', handleOpenSidebar);
    return () => window.removeEventListener('open-mobile-sidebar', handleOpenSidebar);
  }, []);

  // Auth check effect - runs once on mount
  useEffect(() => {
    const skipAuth = DeveloperSettings.shouldSkipAuth();
    const needsLogin = !isAuthenticated && !skipAuth;
    console.log('[AppShell] Auth check - isAuthenticated:', isAuthenticated, 'skipAuth:', skipAuth, 'needsLogin:', needsLogin);
    setRequiresLogin(needsLogin);
    setAuthCheckComplete(true);
  }, [isAuthenticated]);

  // Load force desktop view setting on mount
  useEffect(() => {
    const isForced = DeveloperSettings.shouldForceDesktopView();
    setForceDesktopView(isForced);
  }, []);

  // Fetch campaigns, contacts, and organizations on mount - only once
  // These provide the foundation data for all app features (especially expense allocation tables)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCampaigns();
    fetchContacts();
    fetchOrganizations();
  }, []); // Empty deps: intentionally run only once on mount

  // Fetch active route when campaign is selected (persists across sessions via PostgreSQL)
  // Also loads full route data so Review Routes tab has data immediately
  useEffect(() => {
    if (selectedCampaignId) {
      console.log('[AppShell] Fetching active route for campaign:', selectedCampaignId);

      // Fetch route history (non-blocking)
      fetchRouteHistory(selectedCampaignId);

      // Fetch active route ID, then load full route data
      const loadActiveRouteData = async () => {
        try {
          const activeRouteId = await fetchActiveRoute(selectedCampaignId);
          if (activeRouteId) {
            console.log('[AppShell] Loading route data for active route:', activeRouteId);
            await loadRoute(activeRouteId);
          }
        } catch (error) {
          console.error('[AppShell] Failed to load active route:', error);
          // Non-blocking - user can still use the app
        }
      };

      loadActiveRouteData();
    }
  }, [selectedCampaignId, fetchActiveRoute, fetchRouteHistory, loadRoute]);

  // ===== CONDITIONAL RETURNS AFTER ALL HOOKS =====

  // Show loading while checking auth
  if (!authCheckComplete) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-dark-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  // Redirect to login if auth required
  if (requiresLogin) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location.pathname }} replace />;
  }

  // Toggle desktop view
  const toggleDesktopView = () => {
    const newValue = !forceDesktopView;
    DeveloperSettings.setDeveloperMode(true);
    DeveloperSettings.setForceDesktopView(newValue);
    setForceDesktopView(newValue);
    window.location.reload();
  };

  // Handle logout
  const handleLogout = () => {
    authLogout();
    navigateTo(ROUTES.LOGIN);
  };

  // Handle timer minimize/restore
  const handleTimerMinimize = () => {
    setIsTimerVisible(false);
    localStorage.setItem('floating-timer-visible', 'false');
  };

  const handleTimerRestore = () => {
    setIsTimerVisible(true);
    localStorage.setItem('floating-timer-visible', 'true');
  };

  /**
   * Get active tab from current route (supports nested routes)
   * Matches route patterns like /app/campaigns/* to highlight "Campaign Management" tab
   */
  const getActiveTab = (pathname: string): AppTab => {
    // Match route patterns with startsWith to support nested routes
    if (pathname.startsWith('/app/home')) return 'Home';
    if (pathname.startsWith('/app/dashboard')) return 'Dashboard';
    if (pathname.startsWith('/app/campaigns')) return 'Campaign Management';
    if (pathname.startsWith('/app/organizations')) return 'Organization Management';
    if (pathname.startsWith('/app/addresses')) return 'Address Management';
    if (pathname.startsWith('/app/routes')) return 'Route Planning';
    if (pathname.startsWith('/app/review-routes')) return 'Review Routes';
    if (pathname.startsWith('/app/contacts')) return 'Contact Management';
    if (pathname.startsWith('/app/labels')) return 'Label Generator';
    if (pathname.startsWith('/app/execution')) return 'Execution Tracking';
    if (pathname.startsWith('/app/handwrytten')) return 'Handwrytten Export';
    if (pathname.startsWith('/app/epg')) return 'Event Planning Grid';
    if (pathname.startsWith('/app/timelines')) return 'Timelines';
    if (pathname.startsWith('/app/notes')) return 'Notes';
    if (pathname.startsWith('/app/reports')) return 'Reports';
    if (pathname.startsWith('/app/report-generator')) return 'Report Generator';

    // Default to Organization Management if no match
    return 'Organization Management';
  };

  const activeTab = getActiveTab(location.pathname);

  // Detect if running inside an iframe (panel mode)
  // When in iframe, hide all navigation chrome for clean embedded view
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  /**
   * Handle tab navigation using type-safe navigation
   * Closes mobile sidebar after navigation
   */
  const handleTabClick = (tab: AppTab) => {
    const route = TAB_TO_ROUTE[tab];
    if (route) {
      navigateTo(route);
    }
    setIsMobileSidebarOpen(false);
  };

  // Get selected campaign object
  const selectedCampaign = campaigns?.find(c => c.campaign_id === selectedCampaignId);

  // When running in iframe (panel mode), render only the page content without navigation chrome
  if (isInIframe) {
    return (
      <div className={`h-screen w-screen flex flex-col bg-dark-bg text-dark-text font-sans overflow-hidden selection:bg-brand-500 selection:text-white ${currentTheme}`}>
        {/* Main Content Only - No navigation chrome in iframe/panel mode */}
        <div className="flex-1 bg-gradient-to-br from-dark-bg via-dark-bg/95 to-dark-surface/20 overflow-y-auto overflow-x-hidden overscroll-contain">
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen w-screen flex bg-dark-bg text-dark-text font-sans overflow-hidden selection:bg-brand-500 selection:text-white pt-safe ${currentTheme}`}>
      {/* Toast notifications now handled by ToastContainer in Providers */}
      <StartupLoadingOverlay />
      <SettingsModal
        currentTheme={currentTheme}
        setTheme={setCurrentTheme}
      />
      <CampaignSettingsModal
        open={isCampaignSettingsOpen}
        onClose={() => setIsCampaignSettingsOpen(false)}
        campaignName={selectedCampaign?.campaign_name || ''}
      />

      {/* App Panel - Opens any page as a side panel from AppLauncher */}
      <AppPanel />

      {!isMobile && !isAssistantRoute && (
        <>
          {/* User Stickies - Appears on ALL /app pages via Portal */}
          <UserStickies />

          {/* Tab Stickies - Per-tab sticky notes with PostgreSQL persistence */}
          <TabStickies />

          {/* Pocket Sticky Notes - Goals & Focus of the Week (global, persists across all pages) */}
          <PocketStickyNotes />

          {/* Minimized URL Panels - Persistent dock for minimized link previews (from Weekly Board) */}
          <MinimizedUrlPanelsDock />
          <GlobalLinkPreviewPanel />

          {/* Floating Progress Tracker - For HMC event enrichment and other long operations */}
          <FloatingProgressTracker />

          {/* Floating Timer - Global, persists across all /app pages */}
          {/* Hidden when sticky dock chevron is collapsed to 'hidden' mode */}
          {!isDockHidden && (
            <FloatingTimer isVisible={isTimerVisible} onMinimize={handleTimerMinimize} />
          )}

        </>
      )}

      {/* Timer Restore Button - Shows when timer is minimized */}
      {/* On dashboard: above Focus/Goals pills (bottom-[172px]) */}
      {/* On other pages: directly above Sticky Notes menu (bottom-[68px]) */}
      {/* Hidden when sticky dock chevron is collapsed to 'hidden' mode */}
      {/* Adjusts position when taskbar is visible */}
      {!isMobile && !isAssistantRoute && !isTimerVisible && !isDockHidden && (() => {
        const baseBottom = location.pathname.startsWith('/app/dashboard') ? 172 : 68;
        const taskbarOffset = isTaskbarVisible ? APP_PANEL_TASKBAR_HEIGHT : 0;
        const bottomPosition = baseBottom + taskbarOffset;

        return (
          <button
            onClick={handleTimerRestore}
            className="fixed right-4 z-50 p-3 bg-dark-surface border-2 border-brand-500 rounded-full shadow-md hover:shadow-lg hover:bg-dark-surface/50 transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            style={{ bottom: `${bottomPosition}px` }}
            title="Show Timer"
            aria-label="Restore Timer"
            data-testid="timer-restore-pill"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-500">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </button>
        );
      })()}

      {/* Vertical Icon Sidebar - Desktop only */}
      <VerticalSidebar
        activeTab={activeTab}
        onTabClick={handleTabClick}
        tabIcons={TabIcons}
        tabs={APP_TABS}
      />

      {/* Mobile Sidebar */}
      <MobileSidebar
        isOpen={isMobileSidebarOpen}
        onClose={() => setIsMobileSidebarOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <AppLogo size={28} animate />
            <span className="text-xs font-semibold text-brand-500">Cenari</span>
          </div>
        }
      >
        <div className="flex flex-col h-full">
          {/* Navigation Tabs */}
          <div className="flex-1 p-2 space-y-1 overflow-y-auto">
            {APP_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`flex items-center gap-3 px-4 py-3 text-sm rounded transition-all duration-150 w-full ${
                  activeTab === tab
                    ? 'bg-brand-600 text-white'
                    : 'text-dark-text hover:bg-dark-surface/50'
                }`}
                data-testid='unknown-action-btn'
                data-ux='action'>
                {TabIcons[tab]}
                <span>{tab}</span>
              </button>
            ))}
          </div>

          {/* Utility Buttons at Bottom */}
          <div className="border-t border-dark-border/30 p-2 space-y-1 bg-dark-surface">
            {/* Sync Cache and Dev Log Buttons - Mobile only */}
            <div className="pb-2 mb-1 border-b border-dark-border/30 flex gap-2">
              <SyncButton variant="full" className="flex-1 justify-center" />
              <CopyDevLogButton variant="full" className="flex-1 justify-center" />
            </div>
            {forceDesktopView && (
              <button
                onClick={() => {
                  toggleDesktopView();
                  setIsMobileSidebarOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 text-sm rounded transition-all duration-150 w-full text-purple-400 hover:bg-purple-900/20 bg-purple-900/10 border border-purple-900"
                data-testid='unknown-action-btn'
                data-ux='action'>
                <Monitor size={16} />
                <span>Desktop View Mode</span>
              </button>
            )}
            <button
              onClick={() => {
                openSettings();
                setIsMobileSidebarOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-sm rounded transition-all duration-150 w-full text-dark-text hover:bg-dark-surface/50"
              data-testid='unknown-action-btn'
              data-ux='action'>
              <Settings size={16} />
              <span>Settings</span>
            </button>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm rounded transition-all duration-150 w-full text-dark-text hover:bg-dark-surface/50"
              data-testid='unknown-action-btn'
              data-ux='action'>
              <HelpCircle size={16} />
              <span>Help</span>
            </button>
            <button
              onClick={() => {
                handleLogout();
                setIsMobileSidebarOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 text-sm rounded transition-all duration-150 w-full text-red-400 hover:bg-red-900/20"
              data-testid='mobile-sidebar-logout-button'
              data-ux='action'>
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </MobileSidebar>

      {/* Main Content Column - Everything except the vertical sidebar */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar with Settings/Help/Logout (Desktop only) */}
        <TopBar
          activeTab={activeTab}
          campaignName={selectedCampaign?.campaign_name}
          onOpenSettings={openSettings}
          onLogout={handleLogout}
          onNavigateHome={() => navigateTo(ROUTES.DASHBOARD)}
          onNavigateAssistant={() => navigateTo(ROUTES.ASSISTANT)}
          onNavigateToCampaigns={() => navigateTo(ROUTES.CAMPAIGNS)}
          forceDesktopView={forceDesktopView}
          onToggleDesktopView={toggleDesktopView}
        />

        {/* Phase Selector - Workflow phase filtering (Desktop and Mobile) */}
        {/* Hide on mobile for standalone modules that don't use phase filtering (PUG, EPG, Notes, Reports) */}
        {/* Also hide completely when Focus mode is active */}
        {!focusMode && (() => {
          const isStandaloneRoute =
            location.pathname.startsWith('/app/pug') ||
            location.pathname.startsWith('/app/epg') ||
            location.pathname.startsWith('/app/notes') ||
            location.pathname.startsWith('/app/reports');

          // On standalone routes: hide on mobile, show only on desktop
          // On phase-filtered routes: show on both mobile and desktop
          if (isStandaloneRoute) {
            return (
              <div className="hidden md:flex h-10 items-center px-2 md:px-4 bg-dark-bg/80 backdrop-blur-sm border-b border-dark-border/25 shrink-0">
                <PhaseSelector />
              </div>
            );
          }

          return (
            <div className="flex h-10 items-center px-2 md:px-4 bg-dark-bg/80 backdrop-blur-sm border-b border-dark-border/25 shrink-0">
              <PhaseSelector />
            </div>
          );
        })()}

        {/* Main Content Area */}
        <div className="flex-1 bg-gradient-to-br from-dark-bg via-dark-bg/95 to-dark-surface/20 p-0 md:p-2 pb-20 md:pb-2 overflow-y-auto overflow-x-hidden overscroll-contain relative">
          <Outlet />
        </div>

        {/* App Panel Taskbar - Windows-style taskbar at bottom for minimized AppPanels */}
        {/* Part of flex layout so all content naturally pushes up when visible */}
        <AppPanelTaskbar />

      </div> {/* End Main Content Column */}

      {/* Right Sidebar - Desktop only, collapsible context panel */}
      <RightSidebar />

      {/* Mobile Bottom Toolbar - app-wide primary navigation and tools */}
      <MobileBottomToolbar />
    </div>
  );
};
